import { useInView } from "react-intersection-observer"
import { useState, useRef, useCallback, useEffect } from "react"

export const useStickyProductInfo = () => {
  const { ref, inView } = useInView({
    threshold: 0.3,
    initialInView: undefined,
  })
  const [isScrolling, setIsScrolling] = useState(false)
  const [atTopOfPage, setAtTopOfPage] = useState(true)

  // Track if ATB was initially in view when page loaded
  const initialInViewRef = useRef<boolean | null>(null)
  const [initialInView, setInitialInView] = useState<boolean | null>(null)

  // Track if we should use sticky position
  const [useSticky, setUseSticky] = useState(false)

  // Force description visibility - used for the initially-in-view case
  const [forceShowDescription, setForceShowDescription] = useState(true)

  // Track if we've ever seen the ATB during scroll
  const [hasSeenATB, setHasSeenATB] = useState(false)

  // More robust initialization - wait until component is definitely mounted
  useEffect(() => {
    const timer = setTimeout(() => {
      if (initialInViewRef.current === null) {
        // Store the initial inView state
        initialInViewRef.current = inView
        setInitialInView(inView)

        // If initially in view, we'll force description to always show
        // and enable sticky positioning
        if (inView) {
          setForceShowDescription(true)
          setUseSticky(true)
          setHasSeenATB(true)
        }
      }
    }, 300) // Increased delay for more reliable detection

    return () => clearTimeout(timer)
  }, [inView])

  // Handle scroll events
  const handleScroll = useCallback(() => {
    const mainContainer = document.documentElement

    // Check if we're at the top of the page
    const isAtTop = mainContainer?.scrollTop === 0
    setAtTopOfPage(isAtTop)

    // Always update scrolling state for UI purposes
    const scrolling = mainContainer?.scrollTop > 0
    setIsScrolling(scrolling)

    // Only apply dynamic sticky behavior when ATB was initially NOT in view
    if (initialInView === false) {
      if (scrolling) {
        // If we're scrolling and see the ATB, mark that we've encountered it
        if (inView && !hasSeenATB) {
          setHasSeenATB(true)
          setForceShowDescription(false)
        }

        // Only enable sticky when we've seen the ATB at least once and it's currently in view
        if (inView && hasSeenATB) {
          setUseSticky(true)
        }
      } else if (isAtTop) {
        // Disable sticky when returning to top
        setUseSticky(false)
      }
    }
  }, [initialInView, inView, hasSeenATB])

  useEffect(() => {
    // Don't set up the scroll listener until initialInView is determined
    if (initialInView !== null) {
      window.addEventListener("scroll", handleScroll)
      return () => {
        window.removeEventListener("scroll", handleScroll)
      }
    }
  }, [handleScroll, initialInView])

  // Logic for showing description and attributes:
  // 1. If ATB was initially in view, ALWAYS show description/attributes (forceShowDescription)
  // 2. If ATB was NOT initially in view:
  //    - Show when at the top of the page
  //    - Show when ATB is not in view (even while scrolling)
  //    - Hide when scrolling AND ATB is in view
  const showDescriptionAndAttributes =
    forceShowDescription || // Case 1: ATB was initially visible - always show
    (initialInView === false && (atTopOfPage || !inView)) // Case 2: Show when at top OR when ATB is not visible

  return {
    ref,
    isScrolling,
    useSticky,
    showDescriptionAndAttributes,
  }
}
